import {collection, getFirestore} from 'firebase/firestore';
import {Timestamp} from 'firebase/firestore';
import {addDoc} from 'firebase/firestore';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useUserId} from 'hooks/user/useUserId';
import {useCallback} from 'react';
import type {UserId} from 'types/common';
import type {CourseId} from 'types/common';
import {IS_DEVELOPMENT, toFirebaseSafeObject} from 'utils/general';

interface ProductionErrorLoggerResult {
  /**
   * Reports an error in the production environment to firebase.
   *
   * Attaches metadata about the user and site when the error occurred. In
   * development, this function does nothing.
   *
   * @param error - The error to report.
   * @param additionalMetadata - (Optional) Arbitrary metadata to attach to the
   * error.
   */
  logProductionErrorToFirestore: (
    error: Error,
    additionalMetadata?: Record<string, any>,
  ) => Promise<void>;
}
export function useProductionErrorLogger(): ProductionErrorLoggerResult {
  const userId = useUserId();
  const courseId = useCourseId();

  if (IS_DEVELOPMENT) {
    return {
      logProductionErrorToFirestore: async (
        error: Error,
        additionalMetadata?: Record<string, any>,
      ) => {},
    };
  }

  const logProductionErrorToFirestore = useCallback(
    async (error: Error, additionalMetadata: Record<string, any> = {}) => {
      await logProductionErrorNoHook({
        error,
        courseId,
        userId,
        additionalMetadata,
      });
    },
    [userId, courseId],
  );

  return {
    logProductionErrorToFirestore,
  };
}

interface LogProductionErrorNoHookParams {
  error: Error;
  courseId?: CourseId;
  userId?: UserId;
  additionalMetadata?: Record<string, any>;
}
export async function logProductionErrorNoHook(
  params: LogProductionErrorNoHookParams,
) {
  const {error, courseId, userId, additionalMetadata} = params;
  const db = getFirestore();
  const stackTrace = getStackTrace(error);
  await addDoc(collection(db, 'errorLogs'), {
    url: window.location.href,
    errorMessage: error.message,
    errorStack: stackTrace,
    userId,
    courseId,
    createdAt: Timestamp.now(),
    additionalMetadata,
  });
}

interface LogProductionMessageToFirestoreProps {
  /**
   * The message to log.
   */
  message: string;
  /**
   * Whether to log to the console (only relevant in development).
   */
  alsoLogToConsole?: boolean;
  /**
   * The collection to log to. Defaults to 'errorLogs' (same as
   * logProductionErrorToFirestore).
   */
  logCollection?: string;
  /**
   * Additional metadata to log.
   */
  [additionalField: string]: any;
}

/**
 * Logs a message to the production environment to firebase.
 *
 * Attaches metadata about the user and site when the message occurred. In
 * development, this function does nothing unless alsoLogToConsole is true.
 *
 * @param message - The message to log.
 * @param alsoLogToConsole - Whether to log to the console.
 */
export const logProductionMessageToFirestore = async ({
  message,
  alsoLogToConsole,
  logCollection,
  ...additionalMetadata
}: LogProductionMessageToFirestoreProps) => {
  if (IS_DEVELOPMENT) {
    if (alsoLogToConsole) {
      if (additionalMetadata) {
        console.log(
          message,
          Object.entries(additionalMetadata)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', '),
        );
      } else {
        console.log(message);
      }
    }
    return;
  }

  const db = getFirestore();
  const collectionName = logCollection ?? 'errorLogs';
  await addDoc(collection(db, collectionName), {
    ...toFirebaseSafeObject({
      url: window.location.href,
      logMessage: message,
      ...additionalMetadata,
    }),
    // Don't try to sanitize the Timestamp since it's a native Firebase object.
    createdAt: Timestamp.now(),
  });
};

/**
 * Get the stack trace of an error.
 * @param error - The error to get the stack trace of.
 * @returns The stack trace of the error.
 */
export function getStackTrace(error: Error): string {
  if (Error.captureStackTrace) {
    let stackTrace = '';
    Error.captureStackTrace(error);
    if (error.stack) {
      stackTrace = error.stack;
    }
    return stackTrace;
  } else {
    return '';
  }
}
